<template>
  <ContentSection>
    <Container indent="center" class="mb-8">
      <div class="flex justify-start gap-4">
        <div class="relative">
          <Multiselect
            v-model="jobs"
            :options="jobOptions"
            :required="false"
            :multiple="true"
            :label="$t('team.job')"
            :pills="true"
          />
        </div>
      </div>
      <div class="mt-4 flex flex-wrap gap-4">
        <UiPill
          v-for="job in jobs"
          :key="job"
          :value="job"
          :label="$t('team.job')"
          @click="removeJob(job)"
        />
      </div>
    </Container>
    <Container>
      <div class="mx-auto max-w-7xl">
        <div
          v-for="[jobTitle, employees] in filteredEntries"
          :key="jobTitle"
          class="flex flex-col gap-4"
        >
          <h2
            v-if="jobTitle && employees.length > 0"
            class="text-xl font-bold leading-8 lg:text-2xl lg:leading-9"
          >
            {{ jobTitle }}
          </h2>
          <ul class="-m-2 flex flex-wrap">
            <li
              v-for="employee in employees"
              :key="employee.id"
              class="w-1/2 p-2 sm:w-1/3 md:w-1/4 lg:w-1/5"
            >
              <UiTeamPerson v-bind="employee" />
            </li>
          </ul>
        </div>
      </div>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { JobQuery } from '#gql'

const { locale } = useI18n()
type Props = {
  data: {
    title: string
    entries: Array<{
      id: string
      title: string
      job_description: Array<{
        title: string
        id: string
      }>
      email: string
      phone: string
    }>
  }
}
const props = defineProps<Props>()

const jobs = ref([])
const { data: response } = await useAsyncGql('Job', {
  site: locale,
})
const { terms } = response.value as unknown as Enforce<JobQuery>

const peopleByJob = computed(() => {
  const mapJobPeople = new Map<string, Array<Props['data']['entries'][0]>>()

  const groupedTerms = new Map<
    string,
    { title: string; priority: number; terms: Array<any> }
  >()

  terms.data
    .sort((a, b) => Number(a.priority) - Number(b.priority))
    .forEach((term) => {
      const groupKey = term.group?.id ?? term.id
      const groupTitle = term.group?.title ?? term.title

      if (!groupedTerms.has(groupKey)) {
        groupedTerms.set(groupKey, {
          title: groupTitle,
          priority: Number(term.priority),
          terms: [],
        })
      }

      groupedTerms.get(groupKey)!.terms.push(term)
    })

  groupedTerms.forEach((group) => {
    const people: Array<Props['data']['entries'][0]> = []

    group.terms.forEach((term) => {
      const termPeople = props.data.entries.filter((employee) =>
        employee.job_description.some((job) => job.id === term.id)
      )
      people.push(...termPeople)
    })

    mapJobPeople.set(group.title, people)
  })

  return mapJobPeople
})

const filteredEntries = computed(() => {
  if (jobs.value.length === 0) {
    return peopleByJob.value
  }
  const filteredEntries = new Map<string, Array<any>>()
  jobs.value.forEach((job) => {
    if (peopleByJob.value.has(job)) {
      filteredEntries.set(job, peopleByJob.value.get(job))
    }
  })
  return filteredEntries
})

const removeJob = (job: string) => {
  jobs.value = jobs.value.filter((j) => j !== job)
}

const jobOptions = computed(() =>
  terms.data.map((term) => ({
    label: term.title,
    value: term.title,
  }))
)
</script>
